// Keep this import first: it must run before anything that reports the page address.
import './emailPrefillBootstrap';
import './nativeAppBootstrap';
import React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import RootApp from './App';
import reportWebVitals from './reportWebVitals';
import { store } from './Reducers';
import { Provider } from 'react-redux';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import capacitorStorage, { CapacitorCache } from 'Utils/CapacitorStorage';
import { Amplify } from 'aws-amplify';
import { awsRum } from 'Utils/AwsRum';
import { reportError } from 'Utils/reportError';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { setupAxiosInterceptors } from 'Utils/axiosInterceptor';
import { authService } from 'Utils/AuthService';
import { installErrorCapture } from 'Utils/errorLogStore';
import { AudioRecognitionProvider } from 'AudioRecognition';
import { StartupErrorBoundary, StartupLoadingScreen } from './StartupFallbacks';

const region = process.env.REACT_APP_COGNITO_AWS_REGION;
const cognitoUserPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
const cognitoClientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
// const cognitoGuestRoleArn = process.env.REACT_APP_COGNITO_GUEST_ROLE_ARN;
const awsIdentityPoolId = process.env.REACT_APP_RUM_IDENTITY_POOL_ID;
const awsPinpointApplicationId = process.env.REACT_APP_PINPOINT_APPLICATION_ID;

const theme = createTheme({
  typography: {
    button: {
      textTransform: 'none',
    },
  },
});

const rootNode = document.getElementById('root');

// manualPersist: hold off rehydration until bootstrap() has resolved the
// session and namespaced storage to the current user (capacitorStorage.setUserId).
// Rehydrating before we know who is signed in is what let one account's
// persisted library/progress bleed into the next account on the same device.
const persistor = persistStore(store, { manualPersist: true });

const awsConfig = {
  Auth: {
    Cognito: {
      region: region!,
      userPoolId: cognitoUserPoolId!,
      userPoolClientId: cognitoClientId!,
      identityPoolId: awsIdentityPoolId!,
      // do not add oauth config. OAuth is now handled by react-oidc-context
    },
  },
  Analytics: {
    AWSPinpoint: {
      appId: awsPinpointApplicationId,
      region: region,
    },
  },
};
Amplify.configure(awsConfig);
// no need to use the same sharded cache for amplify - it shards itself
const amplifyCache = new CapacitorCache();
cognitoUserPoolsTokenProvider.setKeyValueStorage(amplifyCache);

const STARTUP_TASK_TIMEOUT_MS = 5000;
const STARTUP_PERSIST_TIMEOUT_MS = 5000;
const SESSION_SCOPING_TIMEOUT_MS = 4000;

const startupNow = () => Math.round(performance.now());

async function runStartupTask<T>(label: string, task: () => Promise<T>): Promise<T | undefined> {
  const startedAt = startupNow();
  console.info(`[startup] ${label} started`);
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<undefined>((resolve) => {
    timeoutId = setTimeout(() => {
      console.warn(
        `[startup] ${label} timed out after ${startupNow() - startedAt}ms; continuing startup.`,
      );
      resolve(undefined);
    }, STARTUP_TASK_TIMEOUT_MS);
  });

  try {
    const result = await Promise.race([task(), timeout]);
    console.info(
      `[startup] ${label} finished in ${startupNow() - startedAt}ms`,
      result === undefined ? { result: 'undefined' } : { result: 'defined' },
    );
    return result;
  } catch (error) {
    console.error(
      `[startup] ${label} failed after ${startupNow() - startedAt}ms; continuing startup.`,
      error,
    );
    return undefined;
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

// Resolve to the promise's value, or undefined if it hasn't settled within ms.
// Used to bound session resolution so rehydration always starts before the
// PersistGate watchdog fails open (worst case: rehydrate unscoped).
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<undefined>((resolve) => {
    timeoutId = setTimeout(() => resolve(undefined), ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timeoutId) clearTimeout(timeoutId);
  });
}

function installStartupErrorLogging() {
  window.addEventListener('error', (event) => {
    reportError(event.error ?? event.message, 'startup.uncaughtError');
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, 'startup.unhandledRejection');
  });
}

function TimedPersistGate({ children }: { children: React.ReactNode }) {
  const [forceOpen, setForceOpen] = React.useState(false);
  const bootstrappedRef = React.useRef(false);

  React.useEffect(() => {
    // Watchdog, not strategy: rehydration is a local read and should finish in
    // milliseconds. This firing means storage is wedged — surface it to RUM
    // and fail open so the app still mounts.
    const timeoutId = setTimeout(() => {
      if (bootstrappedRef.current) return;
      const error = new Error(
        `[startup] PersistGate still not bootstrapped after ${STARTUP_PERSIST_TIMEOUT_MS}ms; rendering without persisted state.`,
      );
      console.warn(error.message);
      awsRum?.recordError(error);
      setForceOpen(true);
    }, STARTUP_PERSIST_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <PersistGate persistor={persistor}>
      {(bootstrapped: boolean) => {
        bootstrappedRef.current = bootstrapped;
        return bootstrapped || forceOpen ? <>{children}</> : <StartupLoadingScreen />;
      }}
    </PersistGate>
  );
}

function renderApp() {
  if (!rootNode) {
    throw new Error('Root node not found');
  }

  console.info(`[startup] renderApp called at ${startupNow()}ms`);
  const root = createRoot(rootNode!);
  root.render(
    <Provider store={store}>
      <TimedPersistGate>
        <StartupErrorBoundary>
          <ThemeProvider theme={theme}>
            <AudioRecognitionProvider>
              <RootApp store={store} />
            </AudioRecognitionProvider>
          </ThemeProvider>
        </StartupErrorBoundary>
      </TimedPersistGate>
    </Provider>,
  );

  reportWebVitals();
}

async function bootstrap() {
  // Buffer error-level logs in memory so the dev-only "copy error logs"
  // affordance on the Roadmap can export them. Install before any other
  // startup work so early errors are captured.
  installErrorCapture();
  installStartupErrorLogging();
  // Setup axios interceptors to handle 401 errors and token refresh.
  setupAxiosInterceptors();
  console.info(`[startup] bootstrap started at ${startupNow()}ms`);

  // Render immediately; PersistGate shows the loading screen until we call
  // persistor.persist() below.
  renderApp();

  // Resolve the session from LOCAL token storage (single-flight — the copy in
  // AuthWrapperProvider shares this same lookup) BEFORE rehydrating persisted
  // redux state, so we can namespace storage to the signed-in user. This is
  // what stops one account's persisted library/progress from rehydrating into
  // the next account on the same device. Token storage (oidcCache/amplifyCache)
  // is a separate, unscoped cache, so scoping the redux singleton here can't
  // starve this very lookup.
  const snapshot = await runStartupTask('Session resolution for storage scoping', () =>
    withTimeout(authService.resolveSession(), SESSION_SCOPING_TIMEOUT_MS),
  );
  capacitorStorage.setUserId(snapshot?.profile?.sub);

  // Warm the Capacitor storage cache in the background. Correctness does not depend on
  // this finishing before render because CapacitorStorage.getItem is pull-through.
  void runStartupTask('Capacitor storage initialization', () => capacitorStorage.init());

  // Storage is now user-scoped; start rehydration.
  persistor.persist();
}

bootstrap().catch((error) => {
  reportError(error, 'startup.fatal');
  if (rootNode) {
    createRoot(rootNode).render(<StartupLoadingScreen />);
  }
});
